import { CronosChain, EthChainId, Wallet } from '@types';
import {
  Crc21,
  Erc20,
  EthGravity,
  Gravity,
  Crc21__factory,
  Erc20__factory,
} from '@config/abi/types';
import { getExtraInfo as getCronosExtraInfo } from '@lib/cronos-chain';
import { calculateGasMargin } from '@lib/utils';
import {
  BytesLike,
  BigNumberish,
  BigNumber,
  ContractTransaction,
  ethers,
  Signer,
} from 'ethers';
import axios from 'axios';

// ERC20 contract allowance and approval

export const getErc20Contract = (
  erc20ContractAddress: string,
  signer: Signer
) => {
  return Erc20__factory.connect(erc20ContractAddress, signer);
};

export const getErc20Allowance = async (
  owner: string,
  spender: string,
  erc20Contract: Erc20
): Promise<BigNumber> => {
  return await erc20Contract.allowance(owner, spender);
};

export const getErc20ApprovalGasEstimate = async (
  spender: string,
  maxAmount: string,
  owner: string,
  erc20Contract: Erc20
): Promise<number> => {
  return (
    await erc20Contract.estimateGas.approve(spender, maxAmount, { from: owner })
  ).toNumber();
};

export const getErc20Approval = async (
  spender: string,
  maxAmount: string,
  owner: string,
  gasAmount: number,
  erc20Contract: Erc20
): Promise<ContractTransaction> => {
  return await erc20Contract.approve(spender, maxAmount, {
    from: owner,
    gasLimit: calculateGasMargin(gasAmount),
  });
};

// Transfer sendtoCosmos

export const ethToCronosTransfer = async (
  amount: BigNumberish,
  tokenContractAddress: string,
  destinationAddress: BytesLike,
  gravityContract: Gravity
): Promise<ContractTransaction> => {
  return await gravityContract.sendToCosmos(
    tokenContractAddress,
    destinationAddress,
    amount
  );
};

// Transfer sendToCronosEth

export const sendToCronosEth = async (
  amount: BigNumberish,
  destinationAddress: string,
  ethGravityContract: EthGravity
): Promise<ContractTransaction> => {
  return await ethGravityContract.sendToCronosEth(destinationAddress, {
    value: amount,
  });
};

// Transfer send_to_chain

export const cronosToEthTransfer = async (
  amount: BigNumberish,
  destinationAddress: string,
  bridgeFees: BigNumberish,
  chain: EthChainId,
  crc21Contract: Crc21
): Promise<ContractTransaction> => {
  return await crc21Contract.send_to_evm_chain(
    destinationAddress,
    amount,
    chain,
    bridgeFees,
    []
  );
};

// Transfer send_to_ibc

export const cronosToIbcTransfer = async (
  amount: BigNumberish,
  destinationAddress: string,
  crc21Contract: Crc21,
  channelId: number
): Promise<ContractTransaction> => {
  return await crc21Contract.send_to_ibc(destinationAddress, amount);
};

// Retrieve crc21 contract details
// TODO: for production, instead of calling endpoint to retrieve crc21 contract, set to tokens.json endpoint

export const getCrc21Contract = (
  crc21ContractAddress: string,
  signer: Signer
) => {
  return Crc21__factory.connect(crc21ContractAddress, signer);
};

export const getCrc21AutoContract = async (
  cronosChain: CronosChain,
  tokenContractAddress: string
): Promise<string> => {
  const endpoint = getCronosExtraInfo(
    cronosChain === CronosChain.mainnet
      ? EthChainId.Cronos
      : EthChainId.CronosGravityTestnet
  ).crc21ContractUrl;
  // getAddress returns address as checksum
  const url = `${endpoint}/cronos/v1/contract_by_denom/gravity${ethers.utils.getAddress(
    tokenContractAddress
  )}`;
  const { data } = await axios.get(url);
  if (!data?.contract) {
    return data?.auto_contract;
  }
  return data?.contract;
};

export const getCrc21ContractDecimals = async (
  crc21Contract: Crc21
): Promise<number> => {
  return await crc21Contract.decimals();
};
